import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { Service, Project, BlogPost } from '@/lib/index';
import { useLanguage } from '@/i18n/LanguageContext';
import useEmblaCarousel from 'embla-carousel-react';

interface ServiceModalProps {
  service: Service | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ServiceModal({ service, open, onOpenChange }: ServiceModalProps) {
  const { t } = useLanguage();

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="relative w-full h-64 -mx-6 -mt-6 mb-6">
          <img
            src={service.image}
            alt={service.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4">
            <Badge className="bg-primary text-primary-foreground font-mono text-lg px-4 py-2">
              ${service.startingPrice.toLocaleString()}+
            </Badge>
          </div>
        </div>

        <DialogHeader>
          <DialogTitle className="text-3xl font-serif">{service.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <p className="text-muted-foreground text-lg leading-relaxed">
            {service.description}
          </p>

          <div>
            <h3 className="text-xl font-semibold mb-4">{t('modal.service.features')}</h3>
            <div className="grid gap-3">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">{t('modal.service.howWeWork')}</h3>
            <p className="text-muted-foreground leading-relaxed">{service.howWeWork}</p>
          </div>

          <div className="bg-accent/50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2">{t('modal.service.warranty')}</h3>
            <p className="text-muted-foreground">{service.warranty}</p>
          </div>

          <div className="pt-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href="#contact">{t('modal.service.cta')}</a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ProjectModalProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectModal({ project, open, onOpenChange }: ProjectModalProps) {
  const { t } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = () => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  };

  const scrollNext = () => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  };

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50 bg-background/80 backdrop-blur-sm p-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {project.images.map((image, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0">
                  <img
                    src={image}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-[400px] object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          {project.images.length > 1 && (
            <>
              <button
                onClick={scrollPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm p-2 rounded-full hover:bg-background transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={scrollNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm p-2 rounded-full hover:bg-background transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {project.images.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-2 rounded-full transition-all ${
                      index === selectedIndex ? 'bg-primary w-8' : 'bg-background/60'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h2 className="text-3xl font-serif mb-4">{project.title}</h2>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                {project.location}, {project.province}
              </Badge>
              <Badge variant="outline" className="font-mono">
                ${project.budget.min.toLocaleString()} - ${project.budget.max.toLocaleString()}
              </Badge>
              <Badge variant="outline">{project.duration}</Badge>
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                {project.style}
              </Badge>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3">{t('modal.project.scopeOfWork')}</h3>
            <ul className="space-y-2">
              {project.scopeOfWork.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">{t('modal.project.challenge')}</h3>
              <p className="text-muted-foreground leading-relaxed">{project.challenge}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">{t('modal.project.solution')}</h3>
              <p className="text-muted-foreground leading-relaxed">{project.solution}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface BlogModalProps {
  post: BlogPost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BlogModal({ post, open, onOpenChange }: BlogModalProps) {
  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50 bg-background/80 backdrop-blur-sm p-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="relative w-full h-80">
          <img
            src={post.thumbnail}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4">
            <Badge className="bg-primary text-primary-foreground">{post.category}</Badge>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div>
            <h1 className="text-4xl font-serif mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span>{post.author}</span>
              <span>•</span>
              <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span>•</span>
              <Badge variant="outline" className="font-mono">{post.readTime}</Badge>
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-semibold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-muted-foreground prose-p:leading-relaxed prose-ul:text-muted-foreground prose-li:my-1 prose-strong:text-foreground prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
